/**
 * The two ends of a sign-in to a cluster console, as functions rather than as
 * steps buried in a component.
 *
 * The flow is: this console asks the control plane to start a sign-in, the
 * control plane writes down the `state`, the `nonce` and the PKCE verifier
 * under a key it hands this browser as a short-lived cookie, and answers with
 * an address at the tenancy's own identity provider. The browser leaves. It
 * comes back to `https://<tenancy domain>/login?code=…&state=…`, and the
 * console hands those two values back — nothing else, because everything else
 * either never left the server or would be a value the caller could substitute.
 *
 * All three functions here are pure, which is the point: what the console does
 * with an address bar is the part that is easy to get subtly wrong and hard to
 * see wrong on screen.
 */

import type { ClusterSignInStart, SignInCallback } from '../api/types';

/** The parameters a return carries. Nothing else in the address is read. */
const CODE = 'code';
const STATE = 'state';

/**
 * Whether this page load is a return from the provider.
 *
 * Both parameters or neither: a `code` without a `state` is not a return this
 * console can finish, and sending it on would ask the server to answer a
 * question about an attempt nobody can identify. An address that is not a
 * return is not an error — it is somebody opening the sign-in screen.
 */
export function readSignInCallback(search: string): SignInCallback | null {
  const params = new URLSearchParams(search);
  const code = params.get(CODE);
  const state = params.get(STATE);
  if (code === null || state === null || code === '' || state === '') return null;
  return { code, state };
}

/**
 * The same address with the return stripped out of it.
 *
 * The code is good for one exchange and the server drops the row the moment it
 * is used, so an address bar still holding it means a reload asks a question
 * whose answer is now "expired" — a refusal about nothing, on top of whatever
 * actually happened. It also keeps the code out of the history entry, the page
 * title bar and anything the browser hands to a subsequent page.
 *
 * Everything else in the query survives: this function knows about a sign-in,
 * not about what else a deployment may put in an address.
 */
export function addressWithoutCallback(address: { pathname: string; search: string }): string {
  const params = new URLSearchParams(address.search);
  params.delete(CODE);
  params.delete(STATE);
  const rest = params.toString();
  return rest === '' ? address.pathname : `${address.pathname}?${rest}`;
}

/** The only two schemes an address of somebody's provider can arrive as. */
const SCHEMES = ['https://', 'http://'];

/**
 * The address the browser may be handed, or `null` if it may not be handed one.
 *
 * The answer comes from our own server and names the tenancy's provider, so
 * this is not distrust of the answer so much as a refusal to navigate to
 * whatever a string happens to be. What it turns away is `javascript:`,
 * `data:` and anything relative — shapes that are not the address of a provider
 * at all, and that a browser would otherwise resolve against this origin, which
 * is the one origin a sign-in must never be sent back into.
 *
 * The scheme itself is the server's business rather than this console's: it
 * refuses an issuer that is not `https`, and it is the side that knows what the
 * tenancy configured. A screen that gets `null` says the answer named no
 * address it can follow.
 */
export function authorizationTarget(answer: ClusterSignInStart): string | null {
  const url = answer.authorization_url;
  return SCHEMES.some((scheme) => url.startsWith(scheme) && url.length > scheme.length)
    ? url
    : null;
}
